import * as React from 'react'; // eslint-disable-line
import styled from 'styled-components';

import { COLOR, FONT, FONT_SIZE, SPEED } from '../../packages/constants';
import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import ItemGroup, {
    ItemGroupWrapper,
    StyledList,
    StyledListItem,
    ItemGroupTitle,
    HorizontalRule,
    StyledGroupItem,
    ItemGroupScroller,
} from './atoms/item-group/item-group';
import { GalleryIcon, StyledGalleryIconWrapper } from '../gallery-icon/gallery-icon';
import { SearchFilter } from '../search-filter/search-filter';

interface Action<TType, TPayload> {
    type: TType;
    payload?: TPayload;
}

interface ItemDataItem {
    type: string;
    title: string;
    description: string;
    iconName: string;
    id: string;
    selected?: boolean;
}

interface ItemDataGroup {
    title: string;
    id: string;
    items: ItemDataItem[];
}

interface ItemDataSection {
    section: 'main' | 'top';
    title: string;
    groups: ItemDataGroup[];
}

export type ItemData = ItemDataSection[];

export enum GalleryBrowserActionTypes {
    SELECTED_ITEM = 'SELECTED_ITEM',
}

export interface GalleryBrowserProps {
    itemData: ItemDataSection[];
    /**
     * The name of the items being displayed, e.g. Chart or Page.
     */
    itemNoun: string;
    /**
     * The plural of the items being displayed, e.g. Charts or Pages.
     * Used for empty states / error messages etc.
     */
    itemNounPlural: string;
    assistAccess: boolean;
    hideFavourites?: boolean;
    hideRecent?: boolean;
    imagePath?: string;
    themeProvider?: {};
    galleryWidth?: string;
    /**
     * External function to dispatch actions to
     */
    actionHandler?: <TType, TPayload, TResponse>(action: Action<TType, TPayload>) => Promise<void | TResponse>;
    hasModernStyles?: boolean;
}

interface StyledContentProps {
    imagePath?: string;
    width?: string;
}

const StyledItemGroup = styled(ItemGroup)``;

const StyledContent = styled.div<StyledContentProps>`
    background-color: ${COLOR.PRIMARY.WHITE};
    height: 100%;
    transition: all ${SPEED.TRANSITION} ease-in;
    width: ${(props: StyledContentProps) => (props.width ? props.width : '660px')};
`;

const StyledGalleryBrowser = styled.div<GalleryBrowserProps>`
    display: flex;
    height: 100%;
    width: auto;

    ${StyledItemGroup} {
        max-width: 240px;
        width: 240px;
        min-width: 240px;
    }
`;

const StyledFilterWrapper = styled.div`
    background-color: ${COLOR.PRIMARY.WHITE};
    padding: 22px;
`;

const StyledMessage = styled.p`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.MEDIUM};
    text-align: center;
    color: ${COLOR.MAIN.FORM_FIELD_DARK};
    padding-top: 18vh;
    width: 100%;
`;

// loop through all of the items and build itemsArray
const getAllItems = (itemData: ItemData) => {
    const arr = [];
    for (let ii = 0; ii < itemData.length; ii++) {
        for (let jj = 0; jj < itemData[ii].groups.length; jj++) {
            for (let kk = 0; kk < itemData[ii].groups[jj].items.length; kk++) {
                const item = itemData[ii].groups[jj].items[kk];
                arr.push(item);
            }
        }
    }
    return arr;
};

const getSelectedItem = (itemData: ItemData) => {
    for (let ii = 0; ii < itemData.length; ii++) {
        for (let jj = 0; jj < itemData[ii].groups.length; jj++) {
            for (let kk = 0; kk < itemData[ii].groups[jj].items.length; kk++) {
                if (itemData[ii].groups[jj].items[kk].selected) {
                    return itemData[ii].groups[jj].items[kk];
                }
            }
        }
    }
    return {};
};

export const GalleryBrowser = React.forwardRef<HTMLDivElement, GalleryBrowserProps>(
    (
        {
            itemData,
            itemNoun,
            itemNounPlural,
            hideFavourites,
            hideRecent,
            assistAccess,
            imagePath,
            galleryWidth,
            actionHandler,
            hasModernStyles,
            ...props
        },
        ref,
    ) => {
        const [groupItemChecked, setGroupItemChecked] = React.useState('all');
        const [filterValue, setFilterValue] = React.useState('');
        const [itemList, setItemList] = React.useState([]);
        const [ignoreScrollIntoView, setIgnoreScrollIntoView] = React.useState(false);
        // tslint:disable-next-line:no-null-keyword
        const filterInputRef = React.useRef<HTMLInputElement>(null);
        // tslint:disable-next-line:no-null-keyword
        const itemWrapperRef = React.useRef<HTMLDivElement>(null);

        // create a flat array of all the items
        const itemsArray = React.useMemo(() => {
            return getAllItems(itemData);
        }, [itemData]);

        const [filteredItems, setFilteredItems] = React.useState<ItemDataItem[]>([]);

        // loop through all the items and unselect each
        const unselectAllItems = () => {
            for (let ii = 0; ii < itemData.length; ii++) {
                for (let jj = 0; jj < itemData[ii].groups.length; jj++) {
                    for (let kk = 0; kk < itemData[ii].groups[jj].items.length; kk++) {
                        const item = itemData[ii].groups[jj].items[kk];
                        item.selected = false;
                    }
                }
            }
        };

        // handle click when user clicks on item icon
        const handleGalleryIconChange = async (item: any) => {
            unselectAllItems();
            item.selected = true;
            setIgnoreScrollIntoView(true);
            if (typeof actionHandler !== 'function') {
                return;
            }
            // call function to dispatch the selected item state to parent
            await actionHandler<GalleryBrowserActionTypes, { itemId: string }, void>({
                type: GalleryBrowserActionTypes.SELECTED_ITEM,
                payload: { itemId: item.id },
            });
        };

        // handle click when user clicks on left column group name
        const handleGroupItemClick = (event: any, items?: any) => {
            setIgnoreScrollIntoView(true);
            setGroupItemChecked(event.target.value);
            if (event.target.value !== 'all') {
                setFilterValue('');
            }
            setItemList(items);
            // @ts-ignore
            itemWrapperRef.current.scrollTop = 0;
        };

        // handle change when user starts typing in the search-filter
        const handleFilterChange = (event: any) => {
            setFilterValue(event.target.value.toLowerCase());
            setGroupItemChecked('all');
        };

        // handle click when user clicks the clear search-filter button
        const handleFilterClearButton = () => {
            setFilterValue('');
            // @ts-ignore
            filterInputRef.current.focus();
        };

        React.useEffect(() => {
            // search-filter the itemsArray data based on the filters input
            const filteredData = itemsArray.filter((item: any) => {
                return Object.keys(item).some(() => item.title.toLowerCase().includes(filterValue));
            });
            setFilteredItems(filteredData);
        }, [itemsArray, filterValue]);

        // scroll to selected item
        React.useEffect(() => {
            const temporaryItemSelected = getSelectedItem(itemData);
            const selectedItem = document.querySelector(`input[value="${(temporaryItemSelected as any).id}"]`);
            // only scroll the view if there is a preselected item, dont scroll if user clicks an item
            if (selectedItem && !ignoreScrollIntoView) {
                setTimeout(() => {
                    selectedItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                }, 200);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps -- run only once
        }, []);

        const topNavItems = itemData.filter((item) => item.section === 'top');
        const mainNavItems = itemData.filter((item) => item.section === 'main');

        return (
            <StyledGalleryBrowser
                imagePath={imagePath}
                assistAccess={assistAccess}
                itemData={itemData}
                itemNoun={itemNoun}
                itemNounPlural={itemNounPlural}
                hideFavourites={hideFavourites}
                hideRecent={hideRecent}
                ref={ref}
                {...props}
            >
                <ItemGroupWrapper {...props}>
                    <StyledList key={`top-nav`}>
                        {topNavItems &&
                            Object.entries(topNavItems).map(([, section]) => {
                                return (
                                    <React.Fragment key={`top-group-item-${section.title}`}>
                                        {Object.entries(section.groups).map(([, group]) => {
                                            return (
                                                <StyledGroupItem
                                                    onChange={(e) => handleGroupItemClick(e, group.items)}
                                                    radioValue={group.id}
                                                    key={`top-group-item-${group.id}`}
                                                    checked={groupItemChecked === group.id}
                                                    assistAccess={assistAccess}
                                                    dataTestId={`gallery-item-group-name__${group.id}`}
                                                >
                                                    {group.title}
                                                </StyledGroupItem>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                    </StyledList>

                    <HorizontalRule />

                    <ItemGroupScroller>
                        <StyledList>
                            <StyledGroupItem
                                onChange={(e) => handleGroupItemClick(e)}
                                radioValue={'all'}
                                checked={groupItemChecked === 'all'}
                                assistAccess={assistAccess}
                                dataTestId={'gallery-item-group-name__all'}
                            >
                                All {itemNounPlural}...
                            </StyledGroupItem>
                            {mainNavItems &&
                                Object.entries(mainNavItems).map(([, section]) => {
                                    return (
                                        <StyledListItem key={`main-group-title-${section.title}`}>
                                            {<ItemGroupTitle>{section.title}</ItemGroupTitle>}
                                            <StyledList>
                                                {Object.entries(section.groups).map(([, group]) => {
                                                    return (
                                                        <StyledGroupItem
                                                            onChange={(e) => handleGroupItemClick(e, group.items)}
                                                            radioValue={group.id}
                                                            key={`main-group-item-${group.id}`}
                                                            checked={groupItemChecked === group.id}
                                                            assistAccess={assistAccess}
                                                            dataTestId={`gallery-item-group-name__${group.id}`}
                                                        >
                                                            {group.title}
                                                        </StyledGroupItem>
                                                    );
                                                })}
                                            </StyledList>
                                        </StyledListItem>
                                    );
                                })}
                        </StyledList>
                    </ItemGroupScroller>
                </ItemGroupWrapper>
                <StyledContent width={galleryWidth}>
                    <StyledFilterWrapper>
                        <SearchFilter
                            placeholder={`Filter by ${itemNoun}`}
                            assistAccess={assistAccess}
                            handleChange={handleFilterChange}
                            handleClearButton={handleFilterClearButton}
                            searchTerm={filterValue}
                            ref={filterInputRef}
                        />
                    </StyledFilterWrapper>
                    <StyledGalleryIconWrapper ref={itemWrapperRef}>
                        {groupItemChecked === 'all' ||
                        groupItemChecked === 'create-a-new-item' ||
                        groupItemChecked === 'recent' ||
                        groupItemChecked === 'favourites' ? (
                            filteredItems.length >= 1 ? (
                                filteredItems.map((item: any) => {
                                    return (
                                        <GalleryIcon
                                            imagePath={imagePath}
                                            iconName={item.iconName}
                                            iconLabel={item.title}
                                            id={item.id}
                                            onChange={() => handleGalleryIconChange(item)}
                                            key={item.id}
                                            assistAccess={assistAccess}
                                            isSelected={item.selected}
                                            dataTestId={`modal-add-${itemNoun}__label-${camelToKebab(item.title)}`}
                                            hasModernStyles={hasModernStyles}
                                        />
                                    );
                                })
                            ) : (
                                <StyledMessage>
                                    {groupItemChecked === 'recent'
                                        ? `No ${itemNounPlural} have been used recently`
                                        : groupItemChecked === 'favourites'
                                        ? 'No favourites have been added'
                                        : `No ${itemNounPlural} found matching "${filterValue}"`}
                                </StyledMessage>
                            )
                        ) : (
                            itemList.map((item: any) => {
                                return (
                                    <GalleryIcon
                                        imagePath={imagePath}
                                        iconName={item.iconName}
                                        iconLabel={item.title}
                                        id={item.id}
                                        onChange={() => handleGalleryIconChange(item)}
                                        key={item.id}
                                        assistAccess={assistAccess}
                                        isSelected={item.selected}
                                        dataTestId={`modal-add-${itemNoun}__label-${camelToKebab(item.title)}`}
                                        hasModernStyles={hasModernStyles}
                                    />
                                );
                            })
                        )}
                    </StyledGalleryIconWrapper>
                </StyledContent>
            </StyledGalleryBrowser>
        );
    },
);
GalleryBrowser.displayName = 'GalleryBrowser';
